import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PackageX, Package, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { money } from "@/lib/constants";

export function ReturnDialog({ open, onOpenChange, poId, onSuccess }) {
  const [payload, setPayload] = useState({ lines: [], po_folio: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qtyMap, setQtyMap] = useState({});
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open || !poId) return;
    (async () => {
      setLoading(true);
      setQtyMap({});
      setReason("");
      setNotes("");
      try {
        const { data } = await api.get(`/purchase-orders/${poId}/returnable-lines`);
        setPayload(data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, poId]);

  function updateQty(poLineId, val, available) {
    let v = val === "" ? "" : Number(val);
    if (Number.isNaN(v)) v = "";
    if (v !== "" && v < 0) v = 0;
    if (v !== "" && v > available) v = available;
    setQtyMap((m) => ({ ...m, [poLineId]: v }));
  }

  const totalUnits = Object.values(qtyMap).reduce((s, v) => s + (Number(v) || 0), 0);
  const totalMoney = (payload.lines || []).reduce((s, l) => {
    const q = Number(qtyMap[l.po_line_id] || 0);
    return s + q * Number(l.unit_cost) * (1 + Number(l.iva_rate || 0));
  }, 0);

  const hasReturnable = (payload.lines || []).some((l) => l.available_to_return > 0);

  async function submit() {
    const entries = Object.entries(qtyMap)
      .filter(([, v]) => Number(v || 0) > 0)
      .map(([po_line_id, v]) => ({ po_line_id, qty_to_return: Number(v) }));
    if (entries.length === 0) { toast.error("Captura al menos una cantidad"); return; }
    if (!reason.trim()) { toast.error("Motivo requerido"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/purchase-returns", {
        purchase_order_id: poId,
        reason: reason.trim(),
        notes: notes.trim() || null,
        lines: entries,
      });
      toast.success(`Devolución creada · ${data.folio} (borrador)`);
      onOpenChange(false);
      onSuccess?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="return-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageX className="h-5 w-5" /> Registrar devolución
          </DialogTitle>
          <DialogDescription>
            Solo líneas inventariables con cantidad disponible pueden devolverse.
            La devolución queda en <b>borrador</b>: al emitirla se genera el movimiento inverso de kardex.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="p-6 text-sm text-muted-foreground text-center">Cargando líneas…</div>
        ) : !hasReturnable ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Esta OC no tiene cantidades disponibles para devolver.</AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="border border-border rounded-md overflow-x-auto">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead>Partida</TableHead>
                    <TableHead className="text-right">Recibido</TableHead>
                    <TableHead className="text-right">Ya devuelto</TableHead>
                    <TableHead className="text-right">Disponible</TableHead>
                    <TableHead className="text-right">Costo</TableHead>
                    <TableHead className="w-32">A devolver</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(payload.lines || []).map((l) => {
                    const disabled = l.available_to_return === 0;
                    return (
                      <TableRow key={l.po_line_id} data-testid={`return-line-${l.po_line_id}`}>
                        <TableCell className="text-sm">
                          <div className="flex items-center gap-2">
                            <Package className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                            <span className="font-medium">{l.description}</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            IVA {Math.round((l.iva_rate || 0) * 100)}% · {l.unit}
                          </div>
                        </TableCell>
                        <TableCell className="text-right mono text-sm">{Number(l.qty_received).toFixed(2)}</TableCell>
                        <TableCell className="text-right mono text-sm">{Number(l.qty_returned).toFixed(2)}</TableCell>
                        <TableCell className="text-right mono text-sm font-medium">{Number(l.available_to_return).toFixed(2)}</TableCell>
                        <TableCell className="text-right mono text-sm">{money(l.unit_cost)}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            max={l.available_to_return}
                            value={qtyMap[l.po_line_id] ?? ""}
                            onChange={(e) => updateQty(l.po_line_id, e.target.value, l.available_to_return)}
                            disabled={disabled || saving}
                            placeholder={disabled ? "—" : "0"}
                            className="h-8"
                            data-testid={`return-line-${l.po_line_id}-qty`}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Motivo *</Label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ej. Defecto de fábrica, error de envío…"
                  data-testid="return-reason"
                />
              </div>
              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="return-notes" />
              </div>
            </div>

            <div className="text-right text-sm">
              <span className="text-muted-foreground">Total unidades:</span>{" "}
              <b className="mono">{totalUnits.toFixed(2)}</b>{" "}
              <span className="mx-2">·</span>
              <span className="text-muted-foreground">Monto teórico c/IVA:</span>{" "}
              <b className="mono" data-testid="return-total">{money(totalMoney)}</b>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            className="btn-tenant"
            onClick={submit}
            disabled={saving || totalUnits === 0 || !hasReturnable}
            data-testid="return-submit"
          >
            {saving ? "Guardando…" : "Crear devolución (borrador)"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
